import React from 'react';
import ReactDOM from 'react-dom/client';
import { message } from 'antd';
import { configureShared, getSharedApiAuthHeaders } from '@ai-content-hub/shared-api';

import App from './App';
import './styles/index.css';
import './i18n'; // Initialize i18n
import { getApiAcceptLanguage } from './i18n';
import { authService } from './services/auth.service';
import { APP_NAME } from '@/config/appName';
import { useAuthStore } from '@/store/authStore';
import { useSelectedTenantStore } from '@/store/selectedTenantStore';
import {
  getEmbedApiToken,
  getEmbedBearerToken,
} from '@/utils/embedApiToken';
import {
  isEmbedParentMessagingActive,
  notifyEmbedParentApiAction,
} from '@/utils/embedParentMessaging';

// EAA-1662: index.html carries a static fallback title for the pre-boot paint; the
// runtime name (env-overridable) wins as soon as the bundle loads.
document.title = APP_NAME;

// Set global app version (injected at build time)
window.APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev';

// EAA-1682: cap the static antd `message` toast queue app-wide. Views that
// mount many pickers at once (e.g. import preview, ~1k rows) can each fire
// their own message.error() on failure, stacking dozens of identical toasts
// on screen. maxCount queues the rest and only ever renders the newest 3.
message.config({ maxCount: 3 });

// EAA-1670: Vite emits `vite:preloadError` when a <link rel="modulepreload">
// for a dynamic chunk fails to load — the stale-deploy case where the router's
// lazyWithRetry import() path may not fire. Force one guarded full reload so the
// tab picks up the new build; the sessionStorage flag stops an infinite loop if
// the chunk is genuinely unreachable (offline / real 404).
window.addEventListener('vite:preloadError', () => {
    if (window.sessionStorage.getItem('vite-preload-reload')) return;
    window.sessionStorage.setItem('vite-preload-reload', '1');
    window.location.reload();
});

// Expose content-type versions globally
import { contentTypeVersions } from './lib/content-types';
import { cleanupStalePreviewSessions } from '@ai-content-hub/content-session';
window.CONTENT_TYPE_VERSIONS = contentTypeVersions;

// Clean up stale preview session entries from localStorage (older than 24h)
cleanupStalePreviewSessions();

// Configure shared package with API settings
configureShared({
    apiBaseUrl: import.meta.env.VITE_AI_API_URL || '',
    h5pServerUrl: import.meta.env.VITE_H5P_URL || '',
    // EAA-1928: static H5P bundle (apps/h5p-core). When set, H5P playback reads
    // its player model from this base and never calls the H5P server — which is
    // what "Unable to load content" was: a failed `GET /h5p/new/play`. Empty =
    // legacy H5P-server path, unchanged.
    h5pStaticBase: import.meta.env.VITE_H5P_STATIC_BASE || '',
    // Embed (/embed/*): tokens live in memory (Bearer JWT or legacy API token), not localStorage staff keys.
    getAuthToken: () =>
      getEmbedBearerToken() ??
      getEmbedApiToken() ??
      authService.getAccessToken(),
    getAcceptLanguage: getApiAcceptLanguage,
    /*
     * EAA-1817: the acting ACH tenant, for the SHARED client too.
     *
     * This app has TWO http clients — its own `apiClient` and `sharedApiClient`
     * (tag/content/program/document services) — and they build headers
     * independently. Setting `X-Tenant-Id` on only the local one produced exactly
     * half a fix: /me/access and the permission endpoints resolved an acting
     * tenant while `/api/program/programs/list/` and `/api/content/documents/`
     * arrived with no header, so the verifier left `request.tenant` null and
     * `IsAdminOrPrivateAccess` refused them with a 403.
     *
     * Same staff exclusion as the local client — see the note there on EAA-1886:
     * staff scope by `?tenant_id=`, members by this header.
     */
    getActingTenantId: () => {
        const authUser = useAuthStore.getState().user;
        if (authUser?.is_staff || authUser?.is_superuser) return null;
        return useSelectedTenantStore.getState().selectedTenantId ?? null;
    },
    onEmbedApiSuccess: ({ method, endpoint, status }) => {
        if (!isEmbedParentMessagingActive()) return;
        notifyEmbedParentApiAction({ method, endpoint, status });
    },
    // EAA-1682: sharedApiClient (tag/content/program/... services) had no
    // refresh-on-401 handling, unlike this app's own apiClient — wire it to
    // the same authService.refreshToken() so a long-idle tab transparently
    // refreshes the access token instead of failing outright.
    // EAA-1762: `null` here makes sharedApiClient dispatch `auth:logout`, so it
    // must mean "the session really ended". Throwing instead on an inconclusive
    // refresh (offline, timeout, 5xx) surfaces the failure to the caller as a
    // normal request error and leaves the session intact.
    onUnauthorized: async () => {
        const outcome = await authService.refreshAccessToken();
        if (outcome.status === 'refreshed') return outcome.token;
        if (outcome.status === 'unavailable') throw new Error(outcome.message);
        return null;
    },
});

// TEMP (uncommitted spike): give H5P media uploads an Authorization header.
//
// WHY: `uploadMedia` (content-core) reads its auth from
// `window.__UPLOAD_GET_HEADERS__`, and ONLY `configureContentApi` installs that.
// This app calls `configureShared` instead, so the global was never set and every
// H5P media upload went out unauthenticated — the backend answered
// `401 {"code":1006,"message":"ERROR_AUTHENTICATION_REQUIRED"}`, which H5P rendered
// verbatim under the file picker. That affects the Step 1 background image too, not
// just the new media field; it simply had not been exercised here before.
//
// `getSharedApiAuthHeaders` is the seam its own doc-comment nominates for exactly
// this ("plus optional window.__UPLOAD_GET_HEADERS__ for XMLHttpRequest uploads"),
// so the token, the acting-tenant header and Accept-Language all stay in one place.
//
// Content-Type MUST be dropped: uploadMedia sets every header it is given
// (upload.ts:301) and the body is FormData, so passing `application/json` would
// override the multipart boundary the browser generates. Version headers are
// omitted because uploadMedia attaches its own.
const uploadWin = window as unknown as {
    __UPLOAD_GET_HEADERS__?: () => Record<string, string>;
};
uploadWin.__UPLOAD_GET_HEADERS__ = () => {
    const headers = getSharedApiAuthHeaders({ omitVersionHeaders: true });
    const { 'Content-Type': _contentType, ...rest } = headers;
    return rest;
};

ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
        <App />
    </React.StrictMode>
);

// EAA-1670: a clean boot means any prior stale-chunk reload succeeded — clear
// the guard so the next deploy can self-heal too (otherwise the flag is spent
// permanently after the first recovery).
window.addEventListener('load', () => {
    window.sessionStorage.removeItem('vite-preload-reload');
});
